import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { logger } from '@utils/logger.service';
import { GlobalConfig, IndividualConfig } from 'ngx-toastr/toastr/toastr-config';
import { catchError, filter, map, take } from 'rxjs/operators';
import { Observable, of, zip } from 'rxjs';
import escape from 'lodash-es/escape';
import isObject from 'lodash-es/isObject';
import { IFBTranslatable } from '@app/shared/modules/translate/services/translate.service';

export interface IFBToast extends Partial<IndividualConfig> {
  titleTranslatable?: IFBTranslatable | string;
  textTranslatable?: IFBTranslatable | string;
  faIcon?: string;
  title?: string;
  icon?: string;
  info?: string;
  text?: string;
}

export const ToastrModuleConfig: Partial<GlobalConfig> = {
  preventDuplicates: true,
  enableHtml: true,
  positionClass: 'toast-top-center',
  timeOut: 3000
};

@Injectable({
  providedIn: 'root'
})
export class ToastsService {
  constructor(
    private ngxToast: ToastrService,
    private translocoService: TranslocoService
  ) {}

  /**
   * Shows special toast with notification layout at the top right corner of the screen
   */
  showReward(msg: IFBToast): void {
    this.generateRewardContent(msg)
      .pipe(take(1))
      .subscribe((content: string) => {
        this.ngxToast.show(content, null, {
          positionClass: 'toast-top-right',
          toastClass: 'ngx-toastr __reward'
        });
      });
  }

  /**
   * Shows error toast
   */
  showError(msg: IFBToast): void {
    this.generateAlertContent(msg).subscribe((content: string) => {
      this.ngxToast.show(content, null, {
        toastClass: 'ngx-toastr __error'
      });
    });
  }

  /**
   * Shows success message toast
   */
  showSuccessMessage(msg: IFBToast): void {
    this.showMessage({ ...msg, faIcon: 'fa-check' });
  }

  /**
   * Shows archive success message toast
   */
  showArchiveSuccessMessage(msg: IFBToast): void {
    this.showMessage({ ...msg, faIcon: 'fa-archive' });
  }

  /**
   * Shows close message toast
   */
  showCloseSuccessMessage(msg: IFBToast): void {
    this.showMessage({ ...msg, faIcon: 'fa-lock' });
  }

  /**
   * Shows delete message toast
   */
  showDeleteSuccessMessage(msg: IFBToast): void {
    this.showMessage({ ...msg, faIcon: 'fa-trash-o' });
  }

  /**
   * Shows update message toast
   */
  showUpdateSuccessMessage(msg: IFBToast): void {
    this.showMessage({ ...msg, faIcon: 'fa-refresh' });
  }

  /**
   * Shows warning message toast
   */
  showWarningMessage(msg: IFBToast): void {
    this.showMessage({ ...msg, faIcon: 'fa-exclamation' });
  }

  /**
   * Shows clean message toast without icon
   */
  showMessage(msg: IFBToast): void {
    this.generateAlertContent(msg).subscribe((translation: string) => {
      this.ngxToast.show(translation);
    });
  }

  private generateRewardContent(msg: IFBToast): Observable<string> {
    const titleTranslatable$ = msg.titleTranslatable ? this.getAsyncTranslation(msg.titleTranslatable) : of(null);
    const textTranslatable$ = msg.textTranslatable ? this.getAsyncTranslation(msg.textTranslatable) : of(null);

    return zip(titleTranslatable$, textTranslatable$).pipe(
      map(([titleTranslated, textTranslated]) => {
        const title = titleTranslated || escape(msg.title);
        const text = textTranslated || escape(msg.text);

        return `
          <div class="toast">
            <div class="toast_icon">${msg.icon}</div>
            <div class="toast_content">
              <div class="toast_title">${title}</div>
              <div class="toast_text">${text}</div>
            </div>
          </div>
        `.trim();
      })
    );
  }

  private generateAlertContent(msg: IFBToast): Observable<string> {
    return this.getAsyncTranslation(msg.textTranslatable).pipe(
      map((translated: string) => {
        const text = translated || escape(msg.text);

        msg.faIcon = msg.faIcon ? `<i class="fa ${msg.faIcon} fa-gradient toast-message_icon"></i>` : '';
        msg.text = text ? `<div class="toast-message_text">${text}</div>` : '';
        msg.info = msg.info ? `<div class="toast-message_info">${msg.info}</div>` : '';

        return `<div class="toast-message_wrapper">${msg.faIcon}${msg.text}${msg.info}</div>`;
      }),
      catchError(e => {
        logger.error('generateAlertContent', e);
        return of('');
      })
    );
  }

  private getAsyncTranslation = (translatable: IFBTranslatable | string): Observable<string> => {
    if (isObject(translatable)) {
      return this.translocoService
        .selectTranslate((translatable as IFBTranslatable).key, (translatable as IFBTranslatable).params)
        .pipe(
          filter(translation => translation !== (translatable as IFBTranslatable).key && Boolean(translation)),
          take(1)
        );
    } else {
      return this.translocoService.selectTranslate(translatable).pipe(
        filter(translation => translation !== translatable && Boolean(translation)),
        take(1)
      );
    }
  };
}
